import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { ModelQuickDropdown } from '../../features/models/ModelQuickDropdown';
import { ConfirmDangerDialog } from '../../features/settings/ConfirmDangerDialog';
import { InsufficientBalanceModal } from '../../features/chat/InsufficientBalanceModal';
import { useOpenSettings } from '../../features/settings/SettingsModalContext';
import { useI18n } from '../../hooks/useI18n';
import { useToast } from '../../hooks/useToast';
import { copyTextToClipboard } from '../../features/chat/MessageActions';
import {
  createImageSessionApi,
  deleteGeneratedImageApi,
  downloadGeneratedImageApi,
  fetchImageLibraryApi,
  fetchImageMessagesApi,
  fetchImagePromptCatalogApi,
  generateImageApi,
  itemsFromAssistantMessage,
  libraryItemKey
} from '../../api/images.api';
import { uploadMediaFilesApi } from '../../api/media.api';
import {
  getModelByKey,
  loadStoredModel,
  resolveImageModelForReferences,
  storeSelectedModel
} from '../../constants/chatImageModels';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';
import { useMyPackage } from '../../hooks/useMyPackage';
import { DEFAULT_LLM_PROVIDER_KEY } from '../../constants/llmProviders';
import { notifyRecentActivityUpsert } from '../../features/activity/recentActivityEvents';
import { ImageComposer } from '../../features/images/ImageComposer';
import { ChatFreeQuotaBanner } from '../../features/chat/ChatFreeQuotaBanner';
import { ImageEmptyState } from '../../features/images/ImageEmptyState';
import { ImageGallery } from '../../features/images/ImageGallery';
import { ImageGallerySkeleton } from '../../features/images/ImageGeneratingTile';
import { ImageThread } from '../../features/images/ImageThread';
import { ImageViewer } from '../../features/images/ImageViewer';
import { isInsufficientBalanceError, resolveImageErrorKey } from '../../features/images/imageErrors';

const MAX_ATTACHMENTS = 5;
const PAGE_SIZE = 24;

const collectThreadItems = (messages = []) => {
  const items = [];
  messages.forEach((message, index) => {
    if (message.role !== 'assistant') return;
    let prompt = '';
    for (let i = index - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') {
        prompt = messages[i].content || messages[i].message || '';
        break;
      }
    }
    items.push(...itemsFromAssistantMessage(message, prompt));
  });
  return items;
};

const triggerBlobDownload = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename || 'risman-image.png';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

export const ImagesPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const toast = useToast();
  const openSettings = useOpenSettings();
  const { models: llmModels } = useLlmCatalog();
  const entitlement = useMyPackage();

  const [catalog, setCatalog] = useState([]);
  const [messages, setMessages] = useState([]);
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryPage, setLibraryPage] = useState(1);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [model, setModel] = useState(() => loadStoredModel('image'));
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [canRetry, setCanRetry] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(sessionId || null);
  const [viewerItem, setViewerItem] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState({ open: false, message: '' });
  const [reloadToken, setReloadToken] = useState(0);
  const loadMoreLockRef = useRef(false);
  const skipLoadSessionIdRef = useRef(null);

  const isSessionView = Boolean(sessionId);
  const activeModel = useMemo(() => getModelByKey('image', model, llmModels), [llmModels, model]);
  const returnPath = sessionId ? `/images/${sessionId}` : '/images';
  const viewerItems = isSessionView ? collectThreadItems(messages) : libraryItems;
  const hasMore = !isSessionView && libraryPage * PAGE_SIZE < libraryTotal;
  const isLibraryEmpty = !loading && !sending && libraryItems.length === 0;
  const isSessionEmpty = !loading && !sending && messages.length === 0;

  useEffect(() => {
    setModel(loadStoredModel('image', llmModels));
  }, [llmModels, location.key]);

  useEffect(() => {
    setActiveSessionId(sessionId || null);
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (sessionId && skipLoadSessionIdRef.current === sessionId) {
        skipLoadSessionIdRef.current = null;
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        if (sessionId) {
          const [promptCatalog, sessionMessages] = await Promise.all([
            fetchImagePromptCatalogApi(),
            fetchImageMessagesApi(sessionId)
          ]);
          if (!cancelled) {
            setCatalog(promptCatalog);
            setMessages(sessionMessages);
          }
        } else {
          const [promptCatalog, library] = await Promise.all([
            fetchImagePromptCatalogApi(),
            fetchImageLibraryApi({ page: 1, pageSize: PAGE_SIZE })
          ]);
          if (!cancelled) {
            setCatalog(promptCatalog);
            setLibraryItems(library.items);
            setLibraryPage(library.page);
            setLibraryTotal(library.totalCount);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(t(resolveImageErrorKey(err, 'imageLoadError')));
          setCanRetry(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId, t, reloadToken]);

  const lastGeneratedImageUrls = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role !== 'assistant') continue;
      const urls = (messages[index].imageUrls || []).filter(Boolean);
      if (urls.length) return urls;
    }
    return [];
  }, [messages]);

  const handleLoadMore = useCallback(async () => {
    if (isSessionView || loadingMore || loadMoreLockRef.current || !hasMore) return;
    loadMoreLockRef.current = true;
    setLoadingMore(true);
    try {
      const nextPage = libraryPage + 1;
      const library = await fetchImageLibraryApi({ page: nextPage, pageSize: PAGE_SIZE });
      setLibraryItems((prev) => {
        const seen = new Set(prev.map(libraryItemKey));
        return [...prev, ...library.items.filter((item) => item.url && !seen.has(libraryItemKey(item)))];
      });
      setLibraryPage(library.page);
      setLibraryTotal(library.totalCount);
    } catch (err) {
      toast.error(t(resolveImageErrorKey(err, 'imageLoadMoreError')));
    } finally {
      setLoadingMore(false);
      loadMoreLockRef.current = false;
    }
  }, [hasMore, isSessionView, libraryPage, loadingMore, t, toast]);

  const applyTemplate = (template) => {
    setSelectedTemplateId(template.id);
    setPrompt(template.prompt);
  };

  const handleAddFiles = async (files) => {
    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) return;
    const selected = files.slice(0, remaining);
    const drafts = selected.map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
      url: ''
    }));
    setAttachments((prev) => [...prev, ...drafts]);
    setUploading(true);
    setError('');
    try {
      const { urls, errors } = await uploadMediaFilesApi(selected);
      setAttachments((prev) => {
        const draftIds = new Set(drafts.map((draft) => draft.id));
        let urlIndex = 0;
        return prev
          .map((item) => {
            if (!draftIds.has(item.id)) return item;
            const url = urls[urlIndex];
            urlIndex += 1;
            if (!url) return null;
            return { ...item, url, previewUrl: url };
          })
          .filter(Boolean);
      });
      if (errors.length) {
        setError(t('attachmentUploadPartialError'));
      }
    } catch (err) {
      setError(t(resolveImageErrorKey(err, 'attachmentUploadFailed')));
      setAttachments((prev) => prev.filter((item) => !drafts.some((draft) => draft.id === item.id)));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (item) => {
    if (item.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(item.previewUrl);
    }
    setAttachments((prev) => prev.filter((entry) => entry.id !== item.id));
  };

  const handleGenerate = async (event) => {
    event?.preventDefault?.();
    const text = prompt.trim();
    const attachedUrls = attachments.map((item) => item.url).filter(Boolean);
    const imageUrls =
      attachedUrls.length > 0 ? attachedUrls : isSessionView ? lastGeneratedImageUrls : [];
    if ((!text && !selectedTemplateId) || sending || uploading) {
      return;
    }

    const resolvedModel = resolveImageModelForReferences(model, imageUrls.length > 0, llmModels);
    const wasNewSession = !sessionId;

    setSending(true);
    setError('');
    setCanRetry(false);
    if (resolvedModel !== model) {
      setModel(resolvedModel);
    }
    storeSelectedModel('image', resolvedModel);

    try {
      let id = sessionId || activeSessionId;
      if (!id) {
        const created = await createImageSessionApi({
          model: resolvedModel,
          provider: activeModel.provider || DEFAULT_LLM_PROVIDER_KEY
        });
        id = created.id;
        setActiveSessionId(id);
        notifyRecentActivityUpsert({
          id: created.id,
          type: 'image',
          title: created.title || t('imageNew'),
          updatedAt: created.updatedAt || new Date().toISOString(),
          isPinned: Boolean(created.isPinned),
          isArchived: Boolean(created.isArchived),
          folderId: created.folderId || null
        });
      }

      const assistant = await generateImageApi({
        imageSessionId: id,
        prompt: text,
        provider: activeModel.provider || DEFAULT_LLM_PROVIDER_KEY,
        model: resolvedModel,
        templateId: selectedTemplateId,
        imageUrls
      });

      const userMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        message: text,
        content: text,
        imageUrls: attachedUrls,
        createdAt: new Date().toISOString()
      };

      notifyRecentActivityUpsert({
        id,
        type: 'image',
        title: text.slice(0, 60) || t('imageNew'),
        updatedAt: new Date().toISOString()
      });

      setPrompt('');
      setSelectedTemplateId(null);
      setAttachments([]);

      if (wasNewSession) {
        skipLoadSessionIdRef.current = id;
        setMessages([userMessage, assistant]);
        navigate(`/images/${id}`, { replace: true });
      } else {
        setMessages((prev) => [...prev, userMessage, assistant]);
      }
    } catch (err) {
      if (isInsufficientBalanceError(err)) {
        setInsufficientBalance({ open: true, message: t('insufficientBalanceFallback') });
      } else {
        setError(t(resolveImageErrorKey(err, 'imageSendError')));
        setCanRetry(true);
      }
    } finally {
      setSending(false);
    }
  };

  const handleView = (item) => {
    if (!isSessionView && item?.imageSessionId) {
      navigate(`/images/${item.imageSessionId}`);
      return;
    }
    setViewerItem(item);
  };

  const handleViewerNavigate = (direction) => {
    if (!viewerItem) return;
    const index = viewerItems.findIndex(
      (entry) => entry.messageId === viewerItem.messageId && entry.url === viewerItem.url
    );
    const next = viewerItems[index + direction];
    if (next) setViewerItem(next);
  };

  const handleDownload = async (item) => {
    try {
      const { blob, filename } = await downloadGeneratedImageApi({
        sessionId: item.imageSessionId,
        messageId: item.messageId,
        url: item.url
      });
      triggerBlobDownload(blob, filename);
    } catch (err) {
      toast.error(t(resolveImageErrorKey(err, 'imageDownloadError')));
    }
  };

  const handleCopyLink = async (item) => {
    try {
      await copyTextToClipboard(item.url);
      toast.success(t('imageCopied'));
    } catch {
      toast.error(t('imageCopyError'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteGeneratedImageApi({
        sessionId: pendingDelete.imageSessionId,
        messageId: pendingDelete.messageId,
        url: pendingDelete.url
      });
      setLibraryItems((prev) =>
        prev.filter((item) => !(item.messageId === pendingDelete.messageId && item.url === pendingDelete.url))
      );
      setLibraryTotal((prev) => Math.max(0, prev - 1));
      setMessages((prev) =>
        prev
          .map((message) => {
            if (message.id !== pendingDelete.messageId) return message;
            return {
              ...message,
              imageUrls: (message.imageUrls || []).filter((url) => url !== pendingDelete.url)
            };
          })
          .filter((message) => message.role !== 'assistant' || (message.imageUrls || []).length > 0)
      );
      if (viewerItem?.messageId === pendingDelete.messageId && viewerItem?.url === pendingDelete.url) {
        setViewerItem(null);
      }
      setPendingDelete(null);
    } catch (err) {
      toast.error(t(resolveImageErrorKey(err, 'imageDeleteError')));
    } finally {
      setDeleting(false);
    }
  };

  const composer = (
    <div className="mx-auto w-full max-w-3xl">
      <ImageComposer
        prompt={prompt}
        onPromptChange={setPrompt}
        onSubmit={handleGenerate}
        onRetry={() => {
          if (prompt.trim() || selectedTemplateId) {
            handleGenerate();
            return;
          }
          setCanRetry(false);
          setReloadToken((value) => value + 1);
        }}
        attachments={attachments}
        referencePreviewUrls={attachments.length > 0 || !isSessionView ? [] : lastGeneratedImageUrls}
        followUp={isSessionView && lastGeneratedImageUrls.length > 0}
        onAddFiles={handleAddFiles}
        onRemoveAttachment={handleRemoveAttachment}
        sending={sending}
        uploading={uploading}
        error={error}
        canRetry={canRetry}
      />
      <ChatFreeQuotaBanner entitlement={entitlement} />
    </div>
  );

  return (
    <DashboardLayout
      startActions={
        <ModelQuickDropdown kind="image" modelKey={model} returnTo={returnPath} onChange={setModel} />
      }
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col overflow-x-hidden">
        {loading ? <ImageGallerySkeleton /> : null}

        {!loading && isSessionView && isSessionEmpty ? (
          <ImageEmptyState
            catalog={catalog}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={applyTemplate}
            composer={composer}
          />
        ) : null}

        {!loading && !isSessionView && isLibraryEmpty ? (
          <ImageEmptyState
            catalog={catalog}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={applyTemplate}
            composer={composer}
          />
        ) : null}

        {!loading && !isSessionView && (!isLibraryEmpty || sending) ? (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-neutral-950 dark:text-white">
                {t('imageSectionTitle')}
              </h1>
              <p className="mt-1 text-sm text-neutral-500">{t('imageSectionHint')}</p>
            </div>
            {composer}
            <ImageGallery
              items={libraryItems}
              loading={false}
              loadingMore={loadingMore}
              generating={sending}
              generatingPrompt={prompt}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onView={handleView}
              onDownload={handleDownload}
              onDelete={setPendingDelete}
              onCopyLink={handleCopyLink}
            />
          </div>
        ) : null}

        {!loading && isSessionView && (!isSessionEmpty || sending) ? (
          <div className="flex h-[calc(100dvh-8rem)] flex-col">
            <ImageThread
              messages={messages}
              sending={sending}
              sendingPrompt={prompt}
              onView={handleView}
              onDownload={handleDownload}
              onDelete={setPendingDelete}
              onCopyLink={handleCopyLink}
            />
            <div className="shrink-0 border-t border-neutral-100 pt-4 dark:border-neutral-900">{composer}</div>
          </div>
        ) : null}
      </div>

      <ImageViewer
        open={Boolean(viewerItem)}
        item={viewerItem}
        items={viewerItems}
        onClose={() => setViewerItem(null)}
        onPrevious={() => handleViewerNavigate(-1)}
        onNext={() => handleViewerNavigate(1)}
        onDownload={handleDownload}
        onDelete={setPendingDelete}
      />

      <ConfirmDangerDialog
        open={Boolean(pendingDelete)}
        title={t('imageDeleteTitle')}
        message={t('imageDeleteConfirm')}
        confirmLabel={t('imageDelete')}
        loading={deleting}
        onClose={() => (deleting ? null : setPendingDelete(null))}
        onConfirm={handleConfirmDelete}
      />

      <InsufficientBalanceModal
        open={insufficientBalance.open}
        message={insufficientBalance.message}
        onClose={() => setInsufficientBalance({ open: false, message: '' })}
        onTopUp={() => {
          setInsufficientBalance({ open: false, message: '' });
          openSettings('packages');
        }}
      />
    </DashboardLayout>
  );
};
